import { Button, Container, Grid, Group, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { X } from 'tabler-icons-react';
import { OpenComponent } from '../../enums/misc.enum';
import { Roles } from '../../enums/roles-enum';
import { removeEmptyPropertiesWithNested } from '../../helpers/filters.helpers';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import { IPaginationMeta } from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import { EntityFilterData, EntityIdFilterData } from '../../type/filter.types';
import { BasicMIDs, RetrieveMidsResponse } from '../../type/mids.types';
import '../admin-table/admin-table.styles.css';
import SearchButton from '../buttons/search-button/search-button.component';
import SelectMerchantFilter from '../filters/select-merchant-filter.component';
import SelectPartnerFilter from '../filters/select-partner-filter.component';
import SelectResellerFilter from '../filters/select-reseller-filter.component';
import XPagination from '../x-components/x-pagination.component';
import XPaper from '../x-components/x-paper.component';
import AssignMids from './assign-mids/assign-mids.component';
import MIDsTable from './components/mids-table';
import UpdateMIDs from './update-mids/update-mids.component';
import ViewMIDs from './view-mid-info.component';

function ViewAllMids() {
  const { auth } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [mids, setMids] = useState<BasicMIDs[]>();
  const axiosPrivate = useAxiosPrivate();
  const [paginationData, setPaginationData] = useState<IPaginationMeta>();
  const [refreshTable, setRefreshTable] = useState(false);
  const [params, setParams] = useState<{
    page: number;
    limit: number;
    partnerId?: string;
    resellerId?: string;
    merchantId?: string;
    midName?: string;
  }>({
    limit: 20,
    page: 1,
  });
  const filterSelectData = useForm<EntityFilterData>({
    initialValues: {
      partnersData: [],
      resellersData: [],
      merchantsData: [],
      branchesData: [],
    },
  });
  const filterValues = useForm<EntityIdFilterData>({
    initialValues: {
      partner: '',
      reseller: '',
      merchant: '',
      branch: '',
    },
  });
  const [componentManager, setComponentManager] = useState(OpenComponent.HOME);
  const [selectedMIDId, setSelectedMIDId] = useState<number | undefined>();
  const isViewOnly =
    auth?.role === Roles.SETTLEMENT || auth?.role === Roles.CHECKER;
  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<number[]>([]);

  const getDynamicFilter = () => {
    let filters: JSX.Element = <></>;

    if (
      auth?.role === Roles.ADMINISTRATOR ||
      auth?.role === Roles.SETTLEMENT ||
      auth?.role === Roles.CHECKER
    ) {
      filters = (
        <Grid.Col xs={12} sm={6}>
          <SelectPartnerFilter
            filterSelectData={filterSelectData}
            filterValues={filterValues}
          />
        </Grid.Col>
      );
    }

    if (
      auth?.role === Roles.PARTNER ||
      auth?.role === Roles.ADMINISTRATOR ||
      auth?.role === Roles.SETTLEMENT ||
      auth?.role === Roles.CHECKER
    ) {
      filters = (
        <>
          {filters}
          <Grid.Col xs={12} sm={6}>
            <SelectResellerFilter
              filterSelectData={filterSelectData}
              filterValues={filterValues}
              isPartnerDependent={
                auth?.role === Roles.ADMINISTRATOR ||
                auth?.role === Roles.SETTLEMENT ||
                auth?.role === Roles.CHECKER
              }
            />
          </Grid.Col>
        </>
      );
    }

    if (
      auth?.role === Roles.PARTNER ||
      auth?.role === Roles.RESELLER ||
      auth?.role === Roles.ADMINISTRATOR ||
      auth?.role === Roles.SETTLEMENT ||
      auth?.role === Roles.CHECKER
    ) {
      filters = (
        <>
          {filters}
          <Grid.Col xs={12} sm={6}>
            <SelectMerchantFilter
              filterSelectData={filterSelectData}
              filterValues={filterValues}
              isResellerDependent={
                auth?.role === Roles.PARTNER ||
                auth?.role === Roles.ADMINISTRATOR ||
                auth?.role === Roles.SETTLEMENT ||
                auth?.role === Roles.CHECKER
              }
            />
          </Grid.Col>
        </>
      );
    }

    return <Grid>{filters}</Grid>;
  };

  const loadMIDs = async () => {
    try {
      setIsLoading(true);

      const response = await axiosPrivate.get('/web/mids', {
        params: removeEmptyPropertiesWithNested(params),
      });

      const data: RetrieveMidsResponse = response.data;
      const mids: BasicMIDs[] = data?.items;

      setPaginationData(data?.metadata);
      return mids;
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-retrieve-mids',
        message:
          error?.error ||
          err.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const paginationRequest = (page: number, limit: number) => {
    setParams((prev) => {
      return {
        ...prev,
        page,
        limit,
      };
    });
  };

  const loadAndSetMIDs = async () => {
    const mids = await loadMIDs();
    setMids(mids);
  };

  useEffect(() => {
    loadAndSetMIDs();
    setIsChecked([]);
    setCheckedAll(false);
  }, [params]);

  useDidMountEffect(() => {
    setParams((prev) => {
      return {
        ...prev,
        partnerId: filterValues.values.partner,
        resellerId: filterValues.values.reseller,
        merchantId: filterValues.values.merchant,
        page: 1,
      };
    });
  }, [filterValues.values]);

  useDidMountEffect(() => {
    setParams((prev) => {
      return {
        ...prev,
        midName: search,
        page: 1,
      };
    });
  }, [search]);

  useDidMountEffect(() => {
    loadAndSetMIDs();
  }, [refreshTable]);

  return (
    <>
      {componentManager === OpenComponent.HOME ? (
        <>
          <Title order={2} my='lg'>
            View all MIDs
          </Title>

          <XPaper>
            <Container p='lg' fluid>
              {getDynamicFilter()}
            </Container>
            {!isViewOnly && (
              <Group p='md' position='right'>
                <Button
                  onClick={() => setComponentManager(OpenComponent.UPDATE_USER)}
                  disabled={isChecked.length !== 1}
                >
                  UPDATE
                </Button>
                <Button
                  onClick={() => setComponentManager(OpenComponent.ASSIGN_MID)}
                  disabled={isChecked.length !== 1}
                >
                  ASSIGN MID
                </Button>
              </Group>
            )}
            <Group p='md' position='right'>
              <SearchButton
                setSearch={setSearch}
                disabled={isLoading}
                search={search}
              />
            </Group>

            <MIDsTable
              mids={mids}
              setComponentManager={setComponentManager}
              setSelectedMIDId={setSelectedMIDId}
              isLoading={isLoading}
              checkedAll={checkedAll}
              setCheckedAll={setCheckedAll}
              isChecked={isChecked}
              setIsChecked={setIsChecked}
              isViewOnly={isViewOnly}
            />

            {paginationData && paginationData.totalPages ? (
              <XPagination
                total={paginationData.totalPages}
                page={paginationData.currentPage}
                limit={params.limit}
                paginationRequest={paginationRequest}
              />
            ) : (
              <></>
            )}
          </XPaper>
        </>
      ) : componentManager === OpenComponent.VIEW_USER && selectedMIDId ? (
        <ViewMIDs
          setComponentManager={setComponentManager}
          selectedMIDId={selectedMIDId}
          setSelectedMIDId={setSelectedMIDId}
        />
      ) : componentManager === OpenComponent.UPDATE_USER &&
        isChecked.length === 1 ? (
        <UpdateMIDs
          midId={isChecked[0]}
          setComponentManager={setComponentManager}
          setRefreshTable={setRefreshTable}
        />
      ) : componentManager === OpenComponent.ASSIGN_MID ? (
        <AssignMids
          setComponentManager={setComponentManager}
          midId={isChecked[0]}
          mids={mids}
        />
      ) : (
        <>Cannot access</>
      )}
    </>
  );
}

export default ViewAllMids;
